import type { CSSProperties } from 'react';
import { Icono } from '@xiltrion/ui/icono';
import { varEtiqueta } from '@xiltrion/ui/paleta';
import { Skeleton } from '@xiltrion/ui/skeleton';
import type { DayApp, MeApp } from '@/api/types';
import { appColor } from '@/app/app-color';
import { LogoApp } from '@/app/LogoApp';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Lo que dice la tarjeta bajo la cifra: la frase de la app o, sin ella, la de la suite. */
export function dayPhrase(d: DayApp) {
  if (d.source === 'APP') return d.text ?? (d.total ? plural(d.total, 'pendiente', 'pendientes') : 'Estás al día');
  return d.total ? plural(d.total, 'aviso sin leer', 'avisos sin leer') : 'Sin avisos nuevos';
}

/**
 * Portal de una app en «Mi día»: tarjeta del color de la app (más saturado que en la paleta y con un degradado que corre el
 * matiz hacia el siguiente apenas más oscuro: oscurecerlo más volvía café los tonos cálidos), el logo de la app grande
 * asomando por la esquina inferior derecha (recortado por la tarjeta, como una marca de agua), cifra grande de lo que
 * espera a la persona, lo más urgente y el enlace para entrar. Toda la tarjeta abre la app (o su lista de pendientes);
 * lo urgente, el registro mismo. El fondo usa el tono claro de la paleta también en modo oscuro (ahí la paleta es más
 * clara), así que el texto va siempre claro: `invert-foreground`, que lo es en los dos modos. El contenido deja libre la
 * franja derecha (`pr-24`) para no pisar el logo.
 */
export function PortalApp({ app, day, failed, href, index }: { app: MeApp; day?: DayApp; failed?: boolean; href: string; index: number }) {
  const color = varEtiqueta(appColor(app));
  const urgent = day?.total && day.urgent?.link ? day.urgent : null;
  const style = {
    '--portal': color,
    backgroundColor: `oklch(from ${color} min(l, 0.62) calc(c + 0.04) h)`,
    backgroundImage: `linear-gradient(150deg, oklch(from ${color} min(l, 0.62) calc(c + 0.04) h), oklch(from ${color} calc(min(l, 0.62) - 0.06) calc(c + 0.05) calc(h + 24)))`,
    animationDelay: `${index * 80}ms`,
    animationDuration: '550ms',
  } as CSSProperties;
  return (
    <div
      style={style}
      className="group relative isolate flex min-h-[196px] flex-col overflow-hidden rounded-2xl p-4 pr-24 text-invert-foreground transition-[translate,box-shadow] duration-300 animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-3 fill-mode-both hover:-translate-y-1 hover:shadow-[0_18px_34px_-14px_color-mix(in_oklch,var(--portal)_80%,transparent)]"
    >
      {/* El logo asoma por la esquina: al pasar el ratón se asoma un poco más. */}
      <LogoApp app={app} className="pointer-events-none absolute -bottom-12 -right-10 -z-10 w-44 transition-transform duration-500 group-hover:-translate-x-1.5 group-hover:-translate-y-1.5 group-hover:-rotate-3" />
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="text-[15px] font-semibold">{app.name}</span>
        <span className="text-[11px] font-medium opacity-85">{app.description}</span>
      </div>
      {day ? (
        <>
          {day.total
            ? <span className="mt-3.5 text-[46px] font-semibold leading-none tracking-[-0.04em] tnum">{day.total.toLocaleString('es-CO')}</span>
            : <Icono nombre="mdi:check-circle-outline" className="mt-3.5 size-11" />}
          <span className="mt-1 text-[12.5px] font-semibold">{dayPhrase(day)}</span>
          {urgent && (
            <a href={urgent.link!} title={urgent.title} className="relative z-10 mt-1.5 inline-flex w-fit max-w-full items-center gap-1 rounded-full bg-invert-foreground/20 px-2 py-px text-[11.5px] transition-colors hover:bg-invert-foreground/30">
              <Icono nombre="mdi:alert-circle-outline" className="size-3.5 shrink-0" />
              <span className="truncate">{urgent.detail ?? urgent.title}</span>
            </a>
          )}
        </>
      ) : failed ? (
        <span className="mt-3.5 text-[12.5px] font-semibold opacity-85">No pudimos leer lo que te espera aquí.</span>
      ) : (
        <div className="mt-3.5 space-y-2"><Skeleton className="h-11 w-14 bg-invert-foreground/20" /><Skeleton className="h-3.5 w-28 bg-invert-foreground/20" /></div>
      )}
      {/* El ::after cubre la tarjeta: clic en cualquier parte abre la app; lo urgente queda encima (z-10). */}
      <a href={day?.link ?? href} className="mt-auto inline-flex items-center gap-1 pt-2.5 text-xs font-semibold after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:rounded-2xl focus-visible:after:ring-2 focus-visible:after:ring-invert-foreground focus-visible:after:ring-inset">
        Abrir {app.name}
        <Icono nombre="mdi:arrow-right" className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
      </a>
    </div>
  );
}
